import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { carryForwardModeOfColumns } from 'components/utils/carryForwardOnContinue';
import {
  CARRIED_FROM_KEY,
  CARRY_FORWARD_INDICATORS,
  carryForwardIndicator,
} from '../utils/carryForward';

function SummaryLine({ Icon, color, children }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 0.5, color,
    }}
    >
      <Icon sx={{ fontSize: '1rem', mt: '1px' }} />
      <Typography variant="caption" sx={{ color: 'inherit', lineHeight: 1.4, textAlign: 'left' }}>
        {children}
      </Typography>
    </Box>
  );
}

/**
 * One or two short lines at the top of a block (Volume, TAV, custom fields), readable
 * on a phone without hovering: which of its fields come from the previous container,
 * and which will go to the next one (see carryForwardIndicator).
 * @param {Object} props
 * @param {Array<[string, string]>} props.fields - [label, indicator] of the block's fields
 * @param {string} [props.source] - the container the carried values come from
 */
export function CarryForwardSummary({ fields, source }) {
  const { t } = useTranslation();
  const labelsOf = (...indicators) => [...new Set(fields
    .filter(([, indicator]) => indicators.includes(indicator))
    .map(([label]) => label))].join(', ');
  const carried = labelsOf(CARRY_FORWARD_INDICATORS.carried);
  const willCarry = labelsOf(CARRY_FORWARD_INDICATORS.willCarry, CARRY_FORWARD_INDICATORS.willCarryIfEmpty);
  const ifEmpty = fields.some(([, indicator]) => indicator === CARRY_FORWARD_INDICATORS.willCarryIfEmpty);
  if (!carried && !willCarry) {
    return null;
  }
  return (
    // Clear of the block's caption in the top right corner
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start', alignSelf: 'stretch', gap: 0.25, pr: 5,
    }}
    >
      {carried && (
        <SummaryLine Icon={SubdirectoryArrowRightIcon} color="info.main">
          {source
            ? t('operation.carry_forward_indicators.carried_from', { source, fields: carried })
            : t('operation.carry_forward_indicators.carried', { fields: carried })}
        </SummaryLine>
      )}
      {willCarry && (
        <SummaryLine Icon={ArrowForwardIcon} color="text.secondary">
          {t(`operation.carry_forward_indicators.${ifEmpty ? 'willCarryIfEmpty' : 'willCarry'}`, {
            fields: willCarry,
          })}
        </SummaryLine>
      )}
    </Box>
  );
}

/**
 * @param {Object} params
 * @param {Object} params.formik - the container form
 * @param {Record<string, *>} [params.carriedValues] - previousItemFormValues
 * @param {Record<string, *>} [params.storedValues] - the form values built from the
 *   container's stored data (getDefaultFormValues without carried values), so that the
 *   summary decides what goes on exactly as pickCarryForwardValues
 * @param {Object} params.mappingConfig - { columns, customFields }
 * @returns {(fields: Array<[string, string]>) => React.ReactNode} the summary of a block,
 *   from its [formKey, label] pairs
 */
export function useCarryForwardSummary({
  formik, carriedValues, storedValues, mappingConfig,
}) {
  const carryForwardModeOf = useMemo(
    () => carryForwardModeOfColumns(mappingConfig.columns),
    [mappingConfig.columns],
  );
  return function carryForwardSummary(blockFields) {
    return (
      <CarryForwardSummary
        source={carriedValues?.[CARRIED_FROM_KEY]}
        fields={blockFields.map(([formKey, label]) => [label, carryForwardIndicator({
          formKey,
          values: formik.values,
          initialValues: formik.initialValues,
          storedValues,
          carriedValues,
          carryForwardModeOf,
          customFields: mappingConfig.customFields,
        })])}
      />
    );
  };
}
